import { throttle } from 'throttle-debounce'
import { createAnimationFrameHandler } from '@/lib/helpers.js'

// Om en seek tar längre än så här antar vi att den aldrig blir klar och
// släpper spärren, så att filmen aldrig kan frysa.
const SEEK_TIMEOUT = 1000

export default () => ({
    activeIndex: null,
    stickyTopOffset: 0,
    children: [],
    video: null,
    targetTime: 0,
    isSeeking: false,
    seekStartedAt: 0,

    init() {
        this.children = this.$refs.track.querySelectorAll('[data-index]')

        this.onSeeked = () => {
            this.isSeeking = false
            this.requestSeek()
        }

        this.$nextTick(() => {
            this.onResize()
            this.onScroll()
        })

        window.addEventListener('resize', this.onResize.bind(this))
        window.addEventListener('scroll', this.onScroll.bind(this))
    },

    calculateStickyOffset() {
        const isLg = window.matchMedia('(min-width: 1024px)').matches
        let offset = 0

        if (isLg) {
            offset = (this.$refs.videos.offsetHeight - window.innerHeight) / -2
        } else {
            offset = Math.max(
                0,
                window.innerHeight / 2 - this.$refs.videos.offsetHeight
            )
        }

        this.stickyTopOffset = offset
    },

    calculateVisibleVideo() {
        const videos = [...(this.$refs.videos?.querySelectorAll('video') ?? [])]
        const visible = videos.find((video) => {
            return video.offsetWidth > 0 && video.offsetHeight > 0
        })

        if (visible === this.video) {
            return
        }

        this.video?.removeEventListener('seeked', this.onSeeked)
        this.video = visible
        this.isSeeking = false
        this.video?.addEventListener('seeked', this.onSeeked)

        // Ladda bara ned den film som faktiskt visas. Blade sätter
        // preload="metadata" på båda; här höjs den synliga till "auto".
        videos.forEach((video) => {
            const preload = video === visible ? 'auto' : 'metadata'

            if (video.preload !== preload) {
                video.preload = preload
            }
        })
    },

    // Seekar mot targetTime, men bara en i taget. Att sätta currentTime
    // medan en seek pågår avbryter den pågående, vilket gör att avkodaren
    // aldrig blir klar och bilden hackar. Vid varje "seeked" tas senaste
    // målet om hand i stället.
    requestSeek() {
        if (!this.video?.duration) {
            return
        }

        if (this.isSeeking) {
            if (Date.now() - this.seekStartedAt < SEEK_TIMEOUT) {
                return
            }

            this.isSeeking = false
        }

        if (Math.abs(this.targetTime - this.video.currentTime) < 0.02) {
            return
        }

        this.isSeeking = true
        this.seekStartedAt = Date.now()
        this.video.currentTime = this.targetTime
    },

    updateVideoCurrentTime() {
        if (!this.video?.duration) {
            return
        }

        const windowHeight = window.innerHeight
        const { top, height } = this.$el.getBoundingClientRect()
        const progress = Math.max(
            0,
            Math.min(1, (windowHeight - top) / (windowHeight + height))
        )

        this.targetTime = progress * this.video.duration

        this.requestSeek()
    },

    calculateActiveIndex() {
        let activeIndex = null

        for (let i = 0; i < this.children.length; i++) {
            const { top, bottom } = this.children[i].getBoundingClientRect()

            if (top <= window.innerHeight && bottom >= window.innerHeight) {
                activeIndex = i

                break
            }
        }

        this.activeIndex = activeIndex
    },

    onResize: throttle(1000 / 60, function () {
        this.calculateStickyOffset()
        this.calculateVisibleVideo()
    }),

    onScroll: throttle(
        1000 / 60,
        createAnimationFrameHandler(function () {
            this.updateVideoCurrentTime()
            this.calculateActiveIndex()
        })
    ),

    goTo(index) {
        this.children[index]?.scrollIntoView({
            behavior: 'smooth',
        })
    },
})
